import { ColumnDef } from '@tanstack/react-table';
import { FiDollarSign, FiFileText, FiMinusCircle, FiPercent, FiPlusCircle, FiRepeat } from 'react-icons/fi';

import { Transaction } from '@/cip/_openapi/portfolio-optimizer';
import { transactionsListColumns } from '@/cip/Transactions/transactionsListColumns';
import { DateCell } from '@/cip/widgets/Table/DateCell';
import { TickerLogo } from '@/components/TickerLogo';

export const TRANSACTION_COLUMN_ORDER = ['tradeDate', 'type', 'transactionDetail', 'amount', 'quantity', 'symbol'];

const transactionIcons = {
    'Transfer In': FiRepeat,
    'Transfer Out': FiRepeat,
    'Security Transfer': FiRepeat,
    Journal: FiRepeat,
    Withdrawals: FiMinusCircle,
    Withdrawal: FiMinusCircle,
    Deposits: FiPlusCircle,
    Addition: FiPlusCircle,
    Dividends: FiDollarSign,
    'Coupons & Interest': FiPercent,
    'Fees & Charges': FiFileText,
};

const moneyFormatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 2,
    minimumFractionDigits: 0,
    signDisplay: 'exceptZero',
});
const quantityFormatter = new Intl.NumberFormat('en-US', { maximumFractionDigits: 6 });

const MissingValue = () => <span className="text-stone-400">—</span>;

export const newAccountTransactionColumns: ColumnDef<Transaction, any>[] = transactionsListColumns.map((column) => {
    switch (column.id) {
        case 'tradeDate':
            return { ...column, cell: ({ getValue }) => <DateCell dateString={getValue()} placeholder="—" /> };
        case 'type':
            return {
                ...column,
                size: 240,
                meta: {
                    ...column.meta,
                    filterOptionsFn: (table) => [...(column.meta?.filterOptionsFn?.(table) ?? [])].sort(),
                },
                cell: ({ getValue }) => {
                    const type = getValue() as string | undefined;
                    if (!type) return <MissingValue />;
                    const Icon = transactionIcons[type as keyof typeof transactionIcons] ?? FiFileText;
                    return (
                        <div className="flex items-center gap-3">
                            <span className="flex size-7 shrink-0 items-center justify-center rounded-md border border-stone-200 text-stone-500">
                                <Icon aria-hidden="true" className="size-4" />
                            </span>
                            {type}
                        </div>
                    );
                },
            };
        case 'amount':
            return {
                ...column,
                header: 'Value',
                cell: ({ getValue }) => {
                    const value = getValue();
                    if (value == null || value === '' || !Number.isFinite(Number(value))) {
                        return (
                            <div className="text-right">
                                <MissingValue />
                            </div>
                        );
                    }
                    const amount = Number(value);
                    return (
                        <div
                            className={`text-right ${amount < 0 ? 'text-red-600' : amount > 0 ? 'text-green-700' : 'text-stone-900'}`}
                        >
                            {moneyFormatter.format(amount)}
                        </div>
                    );
                },
            };
        case 'quantity':
            return {
                ...column,
                cell: ({ getValue }) => {
                    const value = getValue();
                    return (
                        <div className="text-right">
                            {value == null || value === '' || !Number.isFinite(Number(value)) ? (
                                <MissingValue />
                            ) : (
                                quantityFormatter.format(Number(value))
                            )}
                        </div>
                    );
                },
            };
        case 'symbol':
            return {
                ...column,
                cell: ({ getValue }) => {
                    const symbol = getValue() as string | undefined;
                    if (!symbol) return <MissingValue />;
                    return (
                        <div className="flex items-center gap-3 font-semibold text-stone-900">
                            <span className="flex size-7 shrink-0 items-center justify-center rounded-md border border-stone-200 bg-white p-1">
                                <TickerLogo tickerSymbol={symbol} className="max-h-full max-w-full" />
                            </span>
                            {symbol}
                        </div>
                    );
                },
            };
        case 'transactionDetail':
            return { ...column, cell: ({ getValue }) => getValue() || <MissingValue /> };
        default:
            return column;
    }
});
